import { useState, useEffect } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { useProgram, useCreateProgram, useUpdateProgram } from '../hooks/usePrograms';
import { useDances } from '../hooks/useDances';
import type { ProgramDanceSlot } from '../api/danceApi';

export function ProgramBuilderPage() {
  const { programId } = useParams<{ programId?: string }>();
  const navigate = useNavigate();
  const isEditing = !!programId;

  const { data: existingProgram } = useProgram(programId);
  const { data: allDances } = useDances('ALL');
  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [dances, setDances] = useState<ProgramDanceSlot[]>([]);
  const [danceSearch, setDanceSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const blocker = useBlocker(isDirty && !saving);

  useEffect(() => {
    if (existingProgram) {
      setTitle(existingProgram.title);
      setDescription(existingProgram.description || '');
      setIsPublic(existingProgram.isPublic);
      setDances(existingProgram.dances);
    }
  }, [existingProgram]);

  const addDance = (danceId: string) => {
    if (dances.some(d => d.danceId === danceId)) return;
    setIsDirty(true);
    setDances(prev => [...prev, { danceId, position: prev.length + 1 }]);
  };

  const removeDance = (index: number) => {
    setIsDirty(true);
    setDances(prev => prev.filter((_, i) => i !== index).map((d, i) => ({ ...d, position: i + 1 })));
  };

  const moveDance = (index: number, direction: 'up' | 'down') => {
    setIsDirty(true);
    const newDances = [...dances];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newDances.length) return;
    [newDances[index], newDances[swapIndex]] = [newDances[swapIndex], newDances[index]];
    setDances(newDances.map((d, i) => ({ ...d, position: i + 1 })));
  };

  const updateSlotNotes = (index: number, callerNotes: string) => {
    setIsDirty(true);
    setDances(prev => prev.map((d, i) => i === index ? { ...d, callerNotes } : d));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);

    try {
      if (isEditing && programId) {
        await updateProgram.mutateAsync({ programId, data: { title, description, isPublic, dances } });
        setIsDirty(false);
        navigate(`/programs/${programId}`);
      } else {
        const program = await createProgram.mutateAsync({ title, description, dances });
        setIsDirty(false);
        navigate(`/programs/${program.programId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save program');
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    fontSize: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'inherit'
  };

  const filteredDances = allDances?.filter(d =>
    !dances.some(slot => slot.danceId === d.danceId) &&
    (d.title.toLowerCase().includes(danceSearch.toLowerCase()) ||
     d.choreographer?.toLowerCase().includes(danceSearch.toLowerCase()))
  ) || [];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'var(--spacing-xl)' }}>
      <form onSubmit={handleSubmit} className="card" style={{ backgroundColor: 'white', padding: 'var(--spacing-xl)' }}>
        <h2 style={{ marginTop: 0 }}>{isEditing ? 'Edit Program' : 'New Program'}</h2>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 'var(--spacing-sm)' }}>Title *</label>
          <input type="text" required value={title} onChange={e => { setIsDirty(true); setTitle(e.target.value); }} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 'var(--spacing-sm)' }}>Description</label>
          <textarea value={description} onChange={e => { setIsDirty(true); setDescription(e.target.value); }} rows={2} style={inputStyle} />
        </div>

        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={isPublic} onChange={e => { setIsDirty(true); setIsPublic(e.target.checked); }} />
            <span>Make this program public (shareable link)</span>
          </label>
        </div>

        <h3>Dances in Program ({dances.length})</h3>
        {dances.length > 0 && (
          <div style={{ marginBottom: 'var(--spacing-lg)', border: '1px solid #e0e0e0', borderRadius: 'var(--radius-sm)' }}>
            {dances.map((slot, index) => {
              const dance = allDances?.find(d => d.danceId === slot.danceId);
              return (
                <div key={slot.danceId} style={{
                  padding: 'var(--spacing-md)',
                  borderBottom: index < dances.length - 1 ? '1px solid #f0f0f0' : 'none',
                  backgroundColor: index % 2 === 0 ? '#fafafa' : 'white'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <strong>{index + 1}. {dance?.title || slot.danceId}</strong>
                      {dance?.choreographer && <span style={{ color: '#666', fontSize: '0.875rem' }}> by {dance.choreographer}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button type="button" onClick={() => moveDance(index, 'up')} disabled={index === 0} className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}>↑</button>
                      <button type="button" onClick={() => moveDance(index, 'down')} disabled={index === dances.length - 1} className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}>↓</button>
                      <button type="button" onClick={() => removeDance(index)} className="btn btn-danger" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}>×</button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Caller notes for this slot..."
                    value={slot.callerNotes || ''}
                    onChange={e => updateSlotNotes(index, e.target.value)}
                    style={{ ...inputStyle, marginTop: '0.5rem', fontSize: '0.875rem', border: '1px solid #e0e0e0' }}
                  />
                </div>
              );
            })}
          </div>
        )}

        <h3>Add Dances</h3>
        <input
          type="text"
          placeholder="Search dances..."
          value={danceSearch}
          onChange={e => setDanceSearch(e.target.value)}
          style={{ ...inputStyle, marginBottom: 'var(--spacing-md)' }}
        />
        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 'var(--radius-sm)' }}>
          {filteredDances.map(dance => (
            <div
              key={dance.danceId}
              style={{ padding: 'var(--spacing-sm) var(--spacing-md)', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
              onClick={() => addDance(dance.danceId)}
            >
              <strong>{dance.title}</strong>
              {dance.choreographer && <span style={{ color: '#666', fontSize: '0.875rem' }}> by {dance.choreographer}</span>}
              {dance.difficulty && <span className="badge badge-difficulty" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>{dance.difficulty}</span>}
            </div>
          ))}
          {filteredDances.length === 0 && (
            <div style={{ padding: 'var(--spacing-md)', color: '#666', fontStyle: 'italic' }}>
              {danceSearch ? 'No dances match your search.' : 'All available dances are already in the program.'}
            </div>
          )}
        </div>

        {error && (
          <div style={{ marginTop: 'var(--spacing-lg)', padding: 'var(--spacing-md)', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: 'var(--radius-sm)', borderLeft: '4px solid var(--color-danger)' }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 'var(--spacing-xl)', display: 'flex', gap: 'var(--spacing-md)' }}>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Program'}
          </button>
          <button type="button" onClick={() => navigate(isEditing ? `/programs/${programId}` : '/programs')} className="btn btn-secondary">
            Cancel
          </button>
        </div>

        {blocker.state === 'blocked' && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white', padding: 'var(--spacing-xl)',
              borderRadius: 'var(--radius-md)', maxWidth: '400px', width: '90%'
            }}>
              <h3 style={{ marginTop: 0 }}>Unsaved Changes</h3>
              <p>You have unsaved changes. Are you sure you want to leave?</p>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
                <button onClick={() => blocker.reset?.()} className="btn btn-secondary">Stay</button>
                <button onClick={() => blocker.proceed?.()} className="btn btn-danger">Leave</button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
